/* eslint-disable @typescript-eslint/no-unused-vars */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
  FormControl,
  FormHelperText,
  Grid2 as Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'
import { some } from 'lodash'
import React, {
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { updateClientInfo } from 'services/Cart'
import {
  BLACK_FRIDAY_PROMO_CODE,
  FIRST_FACIAL_45_PROMO_CODE,
  FIRST_TIME_PROMOS,
  GIFTORY_PROMO_CODE,
  SAY_HEY_PROMO_CODE,
} from 'utils/constants/Helpers'
import {
  OFFER_CODE_APPLIED_MESSAGE,
  OFFER_CODE_EXIST_MESSAGE,
  OFFER_NOT_APPLIED,
} from 'utils/constants/Messages'
import { cleanPhoneNumber } from 'utils/helper-functions'
import useNavigate from 'utils/navigation/useCustomNavigate'
import { ClientInformation } from 'utils/types/cartTypes'
import { AppContext } from '../../../providers/context/App'
import { ErrorContext } from '../../../providers/context/Error'
import {
  addOfferToCart,
  removeOfferFromCart,
} from '../../../services/PromoCode'
import { getUserAppointments } from '../../../services/User'
import { CartOffer } from '../../../utils/types/promoCodeTypes'

import { ReactComponent as PlusCircleIcon } from '../../../assets/icons/plus-circle-new.svg'
import { ReactComponent as TagIcon } from '../../../assets/icons/Tag.svg'
import { ReactComponent as TrashIcon } from '../../../assets/icons/trash.svg'
import './appointment.scss'

const AppointmentPromo: FC<{}> = (): ReactElement => {
  const { isInfluencerFlow } = useNavigate()
  const { appMasterData, updateData } = useContext(AppContext)
  const [showPromoText, setShowPromoText] = useState(false)
  const [error, setError] = useState('')
  const [promoCodes, setPromoCodes] = useState<CartOffer[]>([])
  const [promoOverride, setPromoOverride] = useState('')
  const [promoText, setPromoText] = useState('')
  const { updateErrorData } = useContext(ErrorContext)

  let specialOfferCode = ''

  const removeCode = async (code: CartOffer) => {
    try {
      const removedCard = await removeOfferFromCart(
        appMasterData.cart.cartId,
        code.id
      )
      if (removedCard.removeCartOffer.cart.id) {
        const updatedCodes = promoCodes.filter((d) => d.id !== code.id)
        setPromoCodes(updatedCodes)
        setPromoText('')
        updateData({
          cart: {
            ...appMasterData.cart,
            summary: removedCard.removeCartOffer.cart.summary,
            isPromoCodeApplied: Boolean(updatedCodes.length > 0),
            promoCodes: updatedCodes,
          },
        })
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPromoText(event.target.value)
    setPromoOverride(event.target.value)
    setError('')
  }

  // Function to Apply the promo to cart and update the state
  const addNewPromoAndUpdate = useCallback(
    async (promoCode: string) => {
      // Disabling Black Friday Promo for member users
      if (
        promoCode === BLACK_FRIDAY_PROMO_CODE &&
        appMasterData.userInfo.is_member
      ) {
        return setError(OFFER_CODE_EXIST_MESSAGE)
      }

      const checkPromoCode =
        appMasterData.location.address.city === 'Williamsburg'
          ? FIRST_FACIAL_45_PROMO_CODE
          : promoCode

      const clientInfo: ClientInformation = {
        email: appMasterData.userInfo.email,
        firstName: appMasterData.userInfo.first_name,
        lastName: appMasterData.userInfo.last_name,
        phoneNumber: '',
      }
      if (appMasterData?.userInfo?.phone_number)
        clientInfo.phoneNumber = cleanPhoneNumber(
          appMasterData?.userInfo?.phone_number
        )

      try {
        await updateClientInfo(appMasterData.cart.cartId, clientInfo, '')

        const promoCodeData = await addOfferToCart(
          appMasterData.cart.cartId,
          checkPromoCode
        )
        if (!promoCodeData.data.addCartOffer) {
          return setError(OFFER_CODE_EXIST_MESSAGE)
        }

        if (!promoCodeData.data.addCartOffer.offer?.applied) {
          return setError(OFFER_NOT_APPLIED)
        }

        const updatedCodes = [
          ...promoCodes,
          promoCodeData.data.addCartOffer.offer,
        ]

        const checkFirstPromoCode =
          (FIRST_TIME_PROMOS.includes(promoCode) &&
            !appMasterData.userInfo.is_member) ||
          !FIRST_TIME_PROMOS.includes(promoCode)

        checkFirstPromoCode && setPromoCodes(updatedCodes)
        checkFirstPromoCode &&
          updateData({
            cart: {
              ...appMasterData.cart,
              summary: promoCodeData.data.addCartOffer.cart.summary,
              isPromoCodeApplied: true,
              promoCodes: updatedCodes,
            },
          })
        setShowPromoText(false)
        setPromoText('')
        setPromoOverride('')
      } catch (e: any) {
        setError(e?.error?.message)
      }
    },
    [
      appMasterData.cart,
      appMasterData.userInfo,
      appMasterData.location.address.city,
      promoCodes,
      updateData,
    ]
  )

  const applyPromo = useCallback(async () => {
    const promoExist = some(promoCodes, { code: promoText.toUpperCase() })

    if (
      [
        SAY_HEY_PROMO_CODE,
        GIFTORY_PROMO_CODE,
        FIRST_FACIAL_45_PROMO_CODE,
        ...FIRST_TIME_PROMOS,
      ].includes(promoText.toUpperCase()) &&
      appMasterData.previousBookedAppointments.length > 0
    ) {
      return setError(OFFER_NOT_APPLIED)
    }

    if (promoExist) {
      return setError(OFFER_CODE_APPLIED_MESSAGE)
    }
    const utmPromo =
      appMasterData.utmSource?.toUpperCase() === SAY_HEY_PROMO_CODE
        ? SAY_HEY_PROMO_CODE
        : null

    let promo: string = ''
    if (promoOverride) {
      promo = promoOverride
    } else if (utmPromo || promoText) {
      promo = utmPromo ?? promoText
    }

    await addNewPromoAndUpdate(promo)
  }, [
    addNewPromoAndUpdate,
    appMasterData.previousBookedAppointments.length,
    appMasterData.utmSource,
    promoCodes,
    promoOverride,
    promoText,
  ])

  const applySpecialOffer = async () => {
    try {
      const userAppointment = await getUserAppointments()
      updateData({ previousBookedAppointments: userAppointment })
      await addNewPromoAndUpdate(specialOfferCode)
    } catch (error: any) {
      updateErrorData(error)
    }
  }

  const removeSpecialOffer = () => {
    const findPromoCodeExist = appMasterData.cart.promoCodes.findIndex(
      (a) => a.code === specialOfferCode
    )
    if (findPromoCodeExist > -1) {
      updateData({
        cart: {
          ...appMasterData.cart,
          promoCodes: appMasterData.cart.promoCodes.filter(
            (a) => a.code !== specialOfferCode
          ),
          isPromoCodeApplied: false,
        },
      })
    }
  }

  const ApplyBtnJSX = useMemo(() => {
    return (
      <InputAdornment position="end">
        <span
          onClick={() => {
            applyPromo().catch((e) => console.error(e))
          }}
          onKeyDown={() => {
            applyPromo().catch((e) => console.error(e))
          }}
          className="apply-btn"
          role="none"
        >
          Apply
        </span>
      </InputAdornment>
    )
  }, [applyPromo])

  useEffect(() => {
    const referCode = appMasterData.referCode
    const completedAppointmentCount =
      appMasterData.userInfo.completed_appointment_count === 0

    // URL offer_code takes priority over location's auto-populated offer code
    if (referCode && completedAppointmentCount && appMasterData.isLoggedIn) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      specialOfferCode = referCode!
    } else if (
      appMasterData.location.enableNewClientPromo &&
      !!appMasterData.location.confirmationScreenPromo.offerCode
    ) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      specialOfferCode =
        appMasterData.location.confirmationScreenPromo.offerCode ?? ''
    }

    if (appMasterData?.isLoggedIn && !appMasterData.isLaserFacial) {
      if (referCode || appMasterData.location.enableNewClientPromo) {
        if (appMasterData.cart.promoCodes.length === 0 && !isInfluencerFlow) {
          applySpecialOffer().catch((e) => console.error(e))
        }
      } else {
        removeSpecialOffer()
      }
    }
  }, [appMasterData.isLoggedIn, appMasterData.location.locationId])

  const shouldApplyPromo = !showPromoText

  return (
    <div className="promo-section">
      <Grid
        size={{
          xs: 12,
        }}
        className="selected-promo"
      >
        <List>
          {appMasterData.cart.promoCodes.length > 0 && (
            <Typography className="promo-text">PROMO CODES</Typography>
          )}
          {appMasterData.cart.promoCodes.map((code) => {
            return (
              <ListItem
                key={code.id}
                secondaryAction={
                  <TrashIcon
                    className="trash-icon"
                    onClick={() => {
                      removeCode(code).catch((e) => console.error(e))
                    }}
                  />
                }
              >
                <ListItemAvatar>
                  <TagIcon width={24} height={24} />
                </ListItemAvatar>
                <ListItemText
                  className="list-text"
                  primary={code.code}
                  secondary={code.name}
                />
              </ListItem>
            )
          })}
        </List>
      </Grid>

      {shouldApplyPromo && (
        <div
          className="add-promo-btn"
          onClick={() => setShowPromoText(true)}
          role="none"
        >
          <PlusCircleIcon />{' '}
          <span className="addpromo-text">Add a promo code</span>
        </div>
      )}

      {showPromoText && (
        <Grid container spacing={3} className="add-promo-field">
          <Grid
            size={{
              xs: 12,
            }}
          >
            <FormControl error={true} variant="standard" fullWidth={true}>
              <TextField
                id="standard-basic"
                label="Promo Code"
                variant="filled"
                fullWidth={true}
                value={promoText}
                onChange={handleChange}
                error={!!error}
                slotProps={{
                  input: {
                    endAdornment: ApplyBtnJSX,
                  },
                }}
              />
              {!!error && (
                <FormHelperText className="error-text">
                  <ErrorOutlineIcon className="err-icon" />
                  <span>{error}</span>
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      )}
    </div>
  )
}

export default React.memo(AppointmentPromo)
